/**
 * Letter Template Editor
 *
 * Create and edit letter templates with three editing modes:
 *  - Rich Text (WYSIWYG via Tiptap)
 *  - HTML Source (raw code editor)
 *  - Preview (rendered by the server for the sample member)
 *
 * Merge fields come from the org's template-variable registry (BE #1220) via
 * VariablePicker, and insertion works across both Rich Text and HTML modes.
 * Saving validates the letter's variables; the preview is the server's render.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FileText, Eye, Code, Plus, Save, X, Loader2, AlertCircle, Type,
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Heading1, Heading2, Undo2, Redo2, Minus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Toggle } from '@/components/ui/toggle';
import lettersApi, { LetterTemplate } from '@/services/lettersApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  VariablePicker,
  TemplatePreviewButton,
  TemplateCheckNotice,
  UnresolvedList,
  templateVariablesApi,
  useVariableRegistry,
  type PreviewResponse,
  type ValidateResponse,
} from '@/components/messaging/templateVariables';

// Tiptap rich text
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extension-placeholder';

// ============================================================================
// TYPES
// ============================================================================

type TemplateType = 'welcome' | 'utr' | 'closure' | 'notice' | 'custom';

interface LetterTemplateEditorProps {
  templateId?: number;
  onClose: () => void;
  onSaved?: (template: any) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TEMPLATE_TYPE_OPTIONS: { value: TemplateType; label: string }[] = [
  { value: 'welcome', label: 'Welcome Letter' },
  { value: 'utr', label: 'Unable to Reach (UTR)' },
  { value: 'closure', label: 'Closure Notice' },
  { value: 'notice', label: 'General Notice' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Mailing-address fields the letters service fills from the member's record
 * (routers/letters.py merge_data). BE #1220 seeds them as system variables, so
 * the picker offers them like any other variable. Against a backend that has
 * not seeded them yet, this editor still offers the ones the registry lacks as
 * a separate group and does not treat them as unknown.
 */
const LETTER_ADDRESS_FIELDS = ['member_address', 'member_city', 'member_state', 'member_zip', 'member_medicaid_id'];

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}|\{([a-zA-Z0-9_]+)\}/g;

/** Every variable key used in the letter, in order, de-duplicated. */
function variablesIn(html: string): string[] {
  const out: string[] = [];
  for (const m of html.matchAll(PLACEHOLDER_RE)) {
    const key = m[1] || m[2];
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}

/** Drop "unknown" findings for address fields the registry does not have yet. */
function withoutLetterFields<T extends { key: string; reason: string }>(items: T[] = [], legacyKeys: string[] = LETTER_ADDRESS_FIELDS): T[] {
  return items.filter((u) => !(legacyKeys.includes(u.key) && u.reason === 'unknown_variable'));
}

const DEFAULT_HTML = `<div style="font-family: Arial, sans-serif; line-height: 1.6;">
  <p><strong>{{organization_name}}</strong></p>
  <p>{{current_date}}</p>
  <p>{{member_full_name}}<br>{{member_address}}<br>{{member_city}}, {{member_state}} {{member_zip}}</p>
  <p>Dear {{member_first_name}},</p>
  <p><!-- Your letter content here --></p>
  <p>Sincerely,</p>
  <p>{{case_manager_name}}<br>{{program_name}}</p>
</div>`;

// ============================================================================
// RICH TEXT TOOLBAR
// ============================================================================

function RichTextToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;

  const ToolBtn = ({ active, onClick, children, title }: { active?: boolean; onClick: () => void; children: React.ReactNode; title: string }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-v2accent-soft text-v2accent-ink'
          : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-md">
      <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
        <Bold className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
        <Italic className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
        <UnderlineIcon className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
        <Strikethrough className="h-4 w-4" />
      </ToolBtn>

      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

      <ToolBtn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
        <Heading1 className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
        <Heading2 className="h-4 w-4" />
      </ToolBtn>

      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

      <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
        <List className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
        <ListOrdered className="h-4 w-4" />
      </ToolBtn>

      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

      <ToolBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
        <AlignLeft className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
        <AlignCenter className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
        <AlignRight className="h-4 w-4" />
      </ToolBtn>

      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

      <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
        <Minus className="h-4 w-4" />
      </ToolBtn>

      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

      <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
        <Undo2 className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
        <Redo2 className="h-4 w-4" />
      </ToolBtn>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function LetterTemplateEditor({ templateId, onClose, onSaved }: LetterTemplateEditorProps) {
  const { t } = useAppTranslation();
  const [check, setCheck] = useState<ValidateResponse | null>(null);
  const [serverPreview, setServerPreview] = useState<PreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState(false);
  // Address fields the registry does not (yet) provide; empty once BE seeds them.
  const { variables: registryVars, loading: registryLoading } = useVariableRegistry('all');
  const legacyAddressFields = registryLoading
    ? LETTER_ADDRESS_FIELDS
    : LETTER_ADDRESS_FIELDS.filter((k) => !registryVars.some((v) => v.key === k));
  // Form state
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState<TemplateType>('custom');
  const [htmlContent, setHtmlContent] = useState(DEFAULT_HTML);

  // UI state
  const [activeTab, setActiveTab] = useState<string>('rich');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isEditing = typeof templateId === 'number';

  // Track which direction the last sync went to avoid loops
  const syncSourceRef = useRef<'rich' | 'html' | null>(null);

  // ── Tiptap editor ──
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        // Preserve inline style so HTML output keeps text-align for print
        defaultAlignment: 'left',
      }),
      Underline,
      Placeholder.configure({ placeholder: 'Start writing your letter...' }),
    ],
    content: htmlContent,
    onUpdate: ({ editor: ed }) => {
      if (syncSourceRef.current === 'html') return;
      syncSourceRef.current = 'rich';
      setHtmlContent(ed.getHTML());
      syncSourceRef.current = null;
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none p-4',
        style: 'min-height: 350px',
      },
    },
  });

  // ── Iframe preview ref ──
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  // ── Sync HTML source → Tiptap when switching tabs ──
  const prevTab = useRef(activeTab);
  useEffect(() => {
    if (prevTab.current === 'html' && activeTab === 'rich' && editor) {
      syncSourceRef.current = 'html';
      editor.commands.setContent(htmlContent);
      syncSourceRef.current = null;
    }
    if (prevTab.current === 'rich' && activeTab === 'html' && editor) {
      // htmlContent is already up-to-date from onUpdate
    }
    prevTab.current = activeTab;
  }, [activeTab, editor, htmlContent]);

  // ── Load existing template ──
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    lettersApi.getTemplate(templateId).then((tpl) => {
      if (cancelled) return;
      setTemplateName(tpl.template_name);
      setTemplateType((tpl.template_type as TemplateType) || 'custom');
      if (tpl.html_content) {
        setHtmlContent(tpl.html_content);
        if (editor) {
          syncSourceRef.current = 'html';
          editor.commands.setContent(tpl.html_content);
          syncSourceRef.current = null;
        }
      }
    }).catch((err) => {
      if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load template');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [templateId]);

  // ── Merge field insertion ──
  const insertMergeField = useCallback((field: string) => {
    const tag = `{{${field}}}`;

    if (activeTab === 'rich' && editor) {
      editor.chain().focus().insertContent(tag).run();
    } else if (activeTab === 'html') {
      const ta = textareaRef.current;
      if (ta) {
        const start = ta.selectionStart ?? htmlContent.length;
        const end = ta.selectionEnd ?? htmlContent.length;
        const updated = htmlContent.slice(0, start) + tag + htmlContent.slice(end);
        setHtmlContent(updated);
        requestAnimationFrame(() => {
          ta.focus();
          const newPos = start + tag.length;
          ta.setSelectionRange(newPos, newPos);
        });
      } else {
        setHtmlContent((prev) => prev + tag);
      }
    }
  }, [activeTab, editor, htmlContent]);

  // ── Detect used merge fields ──
  const usedFields = variablesIn(htmlContent);

  // ── Save ──
  const handleSave = async () => {
    if (!templateName.trim()) { setError('Template name is required'); return; }
    if (!htmlContent.trim()) { setError('Content is required'); return; }

    // Validate the letter's variables. Errors block the save; warnings show.
    // A failed check does not block: the send path still refuses to mail a
    // letter it cannot fill.
    try {
      const res = await templateVariablesApi.validate({ body: htmlContent, applies_to: 'letters' });
      const errors = withoutLetterFields(res.errors, legacyAddressFields);
      const filtered = { ...res, errors, warnings: withoutLetterFields(res.warnings, legacyAddressFields), ok: errors.length === 0 };
      setCheck(filtered);
      if (!filtered.ok) return;
    } catch {
      setCheck(null);
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        template_name: templateName.trim(),
        template_type: templateType,
        html_content: htmlContent,
        merge_fields: usedFields,
      };

      const saved = isEditing
        ? await lettersApi.updateTemplate(templateId!, payload)
        : await lettersApi.createTemplate(payload);

      onSaved?.(saved);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // ── Preview: the server renders the letter for the sample member ──
  useEffect(() => {
    if (activeTab !== 'preview') return;
    let cancelled = false;
    setPreviewError(false);
    templateVariablesApi
      .preview({ body: htmlContent, applies_to: 'letters' })
      .then((res) => {
        if (!cancelled) setServerPreview(res);
      })
      .catch(() => {
        if (!cancelled) {
          setServerPreview(null);
          setPreviewError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, htmlContent]);
  const previewHtml = serverPreview?.body ?? '';
  const previewUnresolved = withoutLetterFields(serverPreview?.unresolved ?? [], legacyAddressFields);

  // Build a full standalone HTML document for the iframe preview.
  // This preserves all formatting (alignment, spacing, fonts) exactly as
  // it will appear when printed via PostGrid/Lob.
  const previewDocument = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #1a1a1a;
    margin: 40px;
    padding: 0;
  }
  h1 { font-size: 24px; margin: 16px 0 8px; }
  h2 { font-size: 20px; margin: 14px 0 6px; }
  h3 { font-size: 16px; margin: 12px 0 4px; }
  p { margin: 0 0 12px; }
  ul, ol { margin: 0 0 12px; padding-left: 24px; }
  li { margin-bottom: 4px; }
  hr { border: none; border-top: 1px solid #ccc; margin: 20px 0; }
  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  s { text-decoration: line-through; }
  /* Preserve Tiptap's text-align styles */
  [style*="text-align: center"] { text-align: center; }
  [style*="text-align: right"] { text-align: right; }
  [style*="text-align: left"] { text-align: left; }
</style>
</head>
<body>${previewHtml}</body>
</html>`;

  // Write into iframe whenever preview content changes
  useEffect(() => {
    if (activeTab !== 'preview') return;
    const iframe = previewIframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(previewDocument);
    doc.close();
  }, [activeTab, previewDocument]);

  // ── Loading state ──
  if (loading) {
    return (
      <Dialog open onOpenChange={() => onClose()}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading template...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isEditing ? 'Edit Letter Template' : 'New Letter Template'}
          </DialogTitle>
        </DialogHeader>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
            <button className="ml-auto" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
          </div>
        )}

        <div className="space-y-4">
          {/* Name and Type */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input id="template-name" placeholder="e.g. Welcome Letter - ECM" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-type">Template Type</Label>
              <Select value={templateType} onValueChange={(v) => setTemplateType(v as TemplateType)}>
                <SelectTrigger id="template-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {TEMPLATE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Merge Fields — the org's template-variable registry */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium">
                {t('templateVariables.letters.mergeFields', 'Merge fields')}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {t('templateVariables.letters.insertAtCursor', 'Inserted at the cursor')}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <VariablePicker appliesTo="all" onInsert={insertMergeField} disabled={activeTab === 'preview'} />
                <TemplatePreviewButton appliesTo="letters" body={htmlContent} html label={t('templateVariables.letters.previewForMember', 'Preview for a member')} />
              </div>
              {!registryLoading && legacyAddressFields.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('templateVariables.letters.addressFields', 'Mailing address (filled from the member record when the letter is sent)')}
                </span>
                {legacyAddressFields.map((field) => (
                  <Badge
                    key={field}
                    variant={usedFields.includes(field) ? 'default' : 'outline'}
                    className="cursor-pointer text-xs hover:bg-primary/80 hover:text-primary-foreground transition-colors"
                    onClick={() => insertMergeField(field)}
                  >
                    <Plus className="h-3 w-3 mr-0.5" />
                    {`{{${field}}}`}
                  </Badge>
                ))}
              </div>
              )}
              <TemplateCheckNotice result={check} />
            </CardContent>
          </Card>

          {/* Rich Text / HTML / Preview Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="rich" className="gap-1.5">
                <Type className="h-4 w-4" />
                Rich Text
              </TabsTrigger>
              <TabsTrigger value="html" className="gap-1.5">
                <Code className="h-4 w-4" />
                HTML Source
              </TabsTrigger>
              <TabsTrigger value="preview" className="gap-1.5">
                <Eye className="h-4 w-4" />
                Preview
              </TabsTrigger>
            </TabsList>

            {/* Rich Text (WYSIWYG) */}
            <TabsContent value="rich" className="mt-3">
              <div className="border rounded-md border-gray-200 dark:border-gray-700 overflow-hidden">
                <RichTextToolbar editor={editor} />
                <EditorContent editor={editor} />
              </div>
            </TabsContent>

            {/* HTML Source */}
            <TabsContent value="html" className="mt-3">
              <Textarea
                ref={textareaRef}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                className="min-h-[400px] font-mono text-sm leading-relaxed"
                placeholder="Enter HTML content for the letter..."
              />
            </TabsContent>

            {/* Preview — rendered in an isolated iframe to preserve all formatting */}
            <TabsContent value="preview" className="mt-3">
              <Card>
                <CardContent className="p-0">
                  <div className="border-b bg-muted/50 px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
                    <span>{t('templateVariables.letters.previewNote', 'Rendered for the sample member, as the printed letter will appear')}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">8.5" × 11"</span>
                  </div>
                  {previewError && <p role="alert" className="px-4 py-2 text-xs text-danger">{t('templateVariables.preview.failed', 'Could not render the preview.')}</p>}
                  {previewUnresolved.length > 0 && (
                    <div className="border-b px-4 py-2">
                      <p className="text-xs font-semibold text-danger">
                        {t('templateVariables.preview.unresolvedTitle', 'Not filled — a send to this member would be refused')}
                      </p>
                      <UnresolvedList items={previewUnresolved} tone="error" />
                    </div>
                  )}
                  <div className="bg-gray-100 dark:bg-gray-900 p-4 flex justify-center">
                    <div className="bg-white shadow-lg" style={{ width: '680px', minHeight: '880px', position: 'relative' }}>
                      <iframe
                        ref={previewIframeRef}
                        title="Letter Preview"
                        className="w-full border-0"
                        style={{ minHeight: '880px', display: 'block' }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="h-4 w-4 mr-2" /> {isEditing ? 'Update Template' : 'Create Template'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LetterTemplateEditor;
